import { useMemo, useState, type ReactNode } from 'react';
import { Chevron } from '../Chevron';

// Deployments hold 1,000+ models and sources, so lists are searched, not shown whole.
const MATCH_LIMIT = 50;

interface FacetSearchProps<T> {
  title: string;
  /** Singular, for "Find a …" and "No … matches." */
  noun: string;
  items: T[];
  /** The group an item counts under. Keep it stable across renders. */
  facet: (item: T) => string;
  /** What a search matches besides the item's group. Keep it stable across renders. */
  text: (item: T) => string;
  /** Renders items; `grouped` when they all share one group, so it needn't be repeated. */
  children: (matches: T[], grouped: boolean) => ReactNode;
}

/** A long list as a count and per-group rows that open onto their items, or a search. */
export function FacetSearch<T>({
  title,
  noun,
  items,
  facet,
  text,
  children,
}: FacetSearchProps<T>) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const needle = query.trim().toLowerCase();
  const facets = useMemo(
    () =>
      [...Map.groupBy(items, facet)].sort((a, b) => b[1].length - a[1].length),
    [items, facet]
  );
  const matches = useMemo(
    () =>
      needle
        ? items.filter(item =>
            `${text(item)} ${facet(item)}`.toLowerCase().includes(needle)
          )
        : [],
    [items, needle, facet, text]
  );
  // Short lists show in full. "Show all" renders every row, so the browser's
  // find works on it, which pagination would break.
  const listed = needle
    ? matches
    : showAll || items.length <= MATCH_LIMIT
      ? items
      : null;
  const shown = listed && (showAll ? listed : listed.slice(0, MATCH_LIMIT));
  const toggle = (count: number) => (
    <button
      type="button"
      className="row-action"
      onClick={() => setShowAll(!showAll)}
    >
      {showAll ? 'Show fewer' : `Show all ${count.toLocaleString()}`}
    </button>
  );

  return (
    <>
      {/* As tall as the search box, so the list doesn't jump when it empties. */}
      <div className="flex min-h-7 flex-wrap items-center gap-3">
        <h2 className="eyebrow">
          {title}:
          <b className="ml-1.5 text-ink">{items.length.toLocaleString()}</b>
        </h2>
        {items.length > 0 && (
          <input
            className="input h-7 min-w-24 max-w-xs text-xs field-sizing-content [&::-webkit-search-cancel-button]:ml-1.5"
            type="search"
            placeholder={`Find a ${noun}`}
            aria-label={`Find a ${noun}`}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        )}
        {items.length > MATCH_LIMIT && !needle && toggle(items.length)}
      </div>
      {items.length === 0 ? (
        <p className="mt-1 text-muted">None.</p>
      ) : (
        <>
          {!listed && (
            <div className="mt-1.5">
              {facets.map(([name, group]) => (
                <div key={name}>
                  <button
                    type="button"
                    className="row-action inline-flex items-center gap-1.5 py-1 text-left"
                    aria-expanded={open === name}
                    onClick={() => setOpen(open === name ? null : name)}
                  >
                    <Chevron open={open === name} size={16} />
                    <span className="w-48 truncate">{name}</span>
                    <span className="w-14 text-right tabular-nums text-ink">
                      {group.length.toLocaleString()}
                    </span>
                  </button>
                  {open === name && (
                    <div className="pb-2 pl-6">{children(group, true)}</div>
                  )}
                </div>
              ))}
            </div>
          )}
          {shown && shown.length > 0 && children(shown, false)}
          {needle && matches.length === 0 && (
            <p className="mt-1 text-muted">No {noun} matches.</p>
          )}
          {needle && matches.length > MATCH_LIMIT && (
            <p className="mt-1 text-muted">
              {showAll
                ? `Showing all ${matches.length.toLocaleString()}. `
                : `Showing ${MATCH_LIMIT} of ${matches.length.toLocaleString()}. `}
              {toggle(matches.length)}
            </p>
          )}
        </>
      )}
    </>
  );
}
